/**
 * Shared API entity types mirroring the backend schemas. Field names follow
 * the wire contract exactly (snake_case) so they map 1:1 to the JSON payloads.
 */

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface UserPublic {
  id: number;
  email: string;
  full_name: string;
  avatar_url: string | null;
  preferred_locale: string;
  timezone: string;
  session_profile: string;
  idle_timeout_minutes: number | null;
  is_superadmin: boolean;
  haulier_id: number | null;
  site_id: number | null;
  permissions: string[];
}

export interface LoadCollection {
  id: number;
  site_id: number | null;
  trailer_id: number | null;
  pallet_count: number | null;
  cartons: number | null;
  collection_reference: string | null;
  collection_date: string | null;
  collection_time: string | null;
  wip_time: string | null;
  wip_ready: boolean;
  preloads: string | null;
  comments: string | null;
  weight_tonnes: number | null;
  non_standard_pallets: boolean;
  tallest_pallet_height: number | null;
  shipping_request_numbers: string | null;
  customer_reference_numbers: string | null;
}

export interface LoadPlanCompatibility {
  level: "ok" | "warning" | "error";
  codes: string[];
}

export interface Load {
  id: number;
  load_ref: string;
  customer_id: number;
  haulier_id: number | null;
  cse_id: number | null;
  site_id: number | null;
  total_pallet_count: number | null;
  total_cartons: number | null;
  destination_address: string | null;
  postcode: string | null;
  delivery_date: string | null;
  arrival_time: string | null;
  depot_reference_number: string | null;
  invoice_reference: string | null;
  dispatched: boolean;
  status: string;
  origin: string;
  direction: string;
  collections: LoadCollection[];
  row_version: number;
  /** Bay/vehicle compatibility of the load's live booking (Phase 11). */
  compatibility?: LoadPlanCompatibility | null;
}

/** A load row on the haulier portal (assigned to the haulier). */
export interface HaulierLoad {
  id: number;
  load_ref: string;
  status: string;
  invoice_reference: string | null;
  customer_name: string;
  customer_address: string | null;
  customer_postcode: string | null;
  collection_date: string | null;
  collection_time: string | null;
  rate_card_no: number | null;
}

export interface CustomerCseAssignment {
  id: number;
  cse_id: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
}

export interface Customer {
  id: number;
  customer_no: string;
  name: string;
  address: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  geocode_status: string | null;
  geocoded_at: string | null;
  default_haulier_id: number | null;
  rate_card_id: number | null;
  cse_id: number | null;
  invoice_id: string | null;
  double_deckers_allowed: boolean;
  default_pallet_type_id: number | null;
  max_pallet_count_per_load: number | null;
  loading_time_minutes: number | null;
  travel_time_minutes: number | null;
  customer_to_collect: boolean;
  phone_numbers: string[];
  contact_emails: string[];
  cse_assignments: CustomerCseAssignment[];
  linked_customer_ids: number[];
  deleted_at: string | null;
}

export interface Haulier {
  id: number;
  haulier_code: string;
  name: string;
  is_active: boolean;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  account_manager_name: string | null;
  account_manager_email: string | null;
  account_manager_phone: string | null;
  deleted_at: string | null;
}

export interface RateCard {
  id: number;
  haulier_id: number;
  effective_from: string;
  effective_to: string | null;
  basis: string;
  distance_band: string | null;
  pallet_band_min: number | null;
  pallet_band_max: number | null;
  notice_hours_threshold: number;
  rate_standard: number;
  rate_emergency: number | null;
  rate_out_of_hours: number | null;
  rate_weekend: number | null;
  currency: string;
  return_mode: string | null;
  return_value: number | null;
}

export interface Cse {
  id: number;
  cse_code: string;
  name: string;
  surname: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  parent_id: number | null;
  on_holiday_from: string | null;
  on_holiday_to: string | null;
  deleted_at: string | null;
}

export type ContactOwnerType = "customer" | "haulier" | "site";

export interface Contact {
  id: number;
  owner_type: ContactOwnerType;
  owner_id: number;
  first_name: string | null;
  surname: string | null;
  phone: string | null;
  email: string | null;
  role: string | null;
  parent_id: number | null;
  is_primary: boolean;
}

export interface ContactInput {
  owner_type: ContactOwnerType;
  owner_id: number;
  first_name?: string | null;
  surname?: string | null;
  phone?: string | null;
  email?: string | null;
  role?: string | null;
  parent_id?: number | null;
  is_primary?: boolean;
}

export interface Site {
  id: number;
  site_code: string;
  name: string;
  address: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  geocode_status: string | null;
  geocoded_at: string | null;
  timezone: string;
  is_depot: boolean;
  bays_count: number;
  /** Planner accent colour for this site's collection legs (hex). */
  color: string | null;
}

export interface Bay {
  id: number;
  site_id: number;
  name: string;
  slot_duration_minutes: number;
  is_active: boolean;
  /** Loading sides at this bay (viewed from behind the standing vehicle). */
  loads_from_rear: boolean;
  loads_from_left: boolean;
  loads_from_right: boolean;
  max_vehicle_length_mm: number | null;
}

export interface BaySchedule {
  id: number;
  bay_id: number;
  weekday: number;
  opens_at: string;
  closes_at: string;
}

export interface BayException {
  id: number;
  bay_id: number;
  exception_date: string;
  is_closed: boolean;
  opens_at: string | null;
  closes_at: string | null;
  reason: string | null;
}

export interface User {
  id: number;
  email: string;
  full_name: string;
  preferred_locale: string;
  timezone: string;
  session_profile: string;
  is_active: boolean;
  idle_timeout_minutes: number | null;
  haulier_id: number | null;
  site_id: number | null;
  group_ids: number[];
  grant_permission_ids: number[];
}

export interface Group {
  id: number;
  code: string;
  name: string;
  description: string | null;
  permission_ids: number[];
}

export interface Permission {
  id: number;
  code: string;
  resource: string;
  action: string;
  display_name_key: string;
}

export interface AppSetting {
  id: number;
  key: string;
  value: string;
  value_type: string;
  description_key: string | null;
}

export interface MailboxAccount {
  id: number;
  protocol: "imap" | "pop3";
  host: string;
  port: number;
  use_tls: boolean;
  username: string;
  secret_ref: string;
  folder: string;
  post_action: "move" | "flag" | "leave";
  is_active: boolean;
}

export type ImportType =
  | "shipping_request"
  | "depot_crystal"
  | "customer"
  | "cse"
  | "haulier"
  | "contact"
  | "invoice";

export const IMPORT_TYPES: ImportType[] = [
  "shipping_request",
  "depot_crystal",
  "customer",
  "cse",
  "haulier",
  "contact",
  "invoice",
];

export interface ImportSource {
  id: number;
  code: string;
  mailbox_account_id: number | null;
  import_type: ImportType;
  subject_filter: string | null;
  sender_allowlist: string[];
  poll_interval_minutes: number;
  is_enabled: boolean;
}

export interface ImportRun {
  id: number;
  origin: "email" | "manual" | "replay";
  import_source_id: number | null;
  message_id: string | null;
  content_hash: string;
  started_at: string;
  finished_at: string | null;
  status: "pending" | "running" | "completed" | "failed";
  rows_total: number;
  rows_applied: number;
  rows_quarantined: number;
  archive_path: string | null;
  correlation_id: string | null;
  error: string | null;
}

/** A reusable field mapping for one import type: many aliases → one field. */
export interface ColumnMappingAlias {
  id: number;
  source_header: string;
}

export interface ColumnMapping {
  id: number;
  import_type: ImportType;
  import_source_id: number | null;
  destination_field: string;
  is_required: boolean;
  transform: string | null;
  sort_order: number;
  aliases: ColumnMappingAlias[];
}

/** Result of analysing an uploaded file before a manual import runs. */
export interface ManualImportPreview {
  import_type: ImportType;
  header_row: number | null;
  header_row_detected: boolean;
  columns: string[];
  field_mapping: Record<string, string | null>;
  required_fields: string[];
  missing_required: string[];
}

export interface ImportRow {
  id: number;
  import_run_id: number;
  row_number: number;
  raw_payload: Record<string, unknown>;
  mapped_payload: Record<string, unknown>;
  outcome: "applied" | "quarantined";
  error_code: string | null;
  error_detail: string | null;
  load_id: number | null;
  resolution: "discarded" | "resolved" | null;
  resolved_by: number | null;
  resolved_at: string | null;
}

export interface SlotOffer {
  start_utc: string;
  end_utc: string;
  available: boolean;
  reasons: string[];
}

export interface AvailableSlots {
  bay_id: number;
  day: string;
  slots: SlotOffer[];
}

export interface Booking {
  id: number;
  bay_id: number;
  load_id: number;
  start_utc: string;
  end_utc: string;
  status: string;
  booked_by: number | null;
}

export interface PlannerSlot {
  start_utc: string;
  end_utc: string;
  start_local: string;
  end_local: string;
}

export interface PlannerBay {
  bay_id: number;
  name: string;
  site_id: number;
  site_name: string;
  site_color: string | null;
  slot_duration_minutes: number;
  slots: PlannerSlot[];
}

export interface PlannerLoad {
  load_id: number;
  load_ref: string;
  direction: string;
  /** The collection leg this card represents (null = load had no legs). */
  leg_id: number | null;
  site_id: number | null;
  site_name: string | null;
  site_color: string | null;
  collection_reference: string | null;
  customer_name: string;
  delivery_time: string | null;
  departure_time: string | null;
  carton_count: number | null;
  pallet_count: number | null;
  destination_address: string | null;
  postcode: string | null;
  haulier_name: string | null;
  haulier_phone: string | null;
  bay_id: number;
  start_utc: string;
  end_utc: string;
  start_local: string;
  end_local: string;
}

export interface PlannerUnassignedLoad {
  load_id: number;
  load_ref: string;
  direction: string;
  status: string;
  /** The collection leg this card represents (null = load had no legs). */
  leg_id: number | null;
  site_id: number | null;
  site_name: string | null;
  site_color: string | null;
  collection_reference: string | null;
  customer_name: string;
  delivery_time: string | null;
  departure_time: string | null;
  collection_time: string | null;
  carton_count: number | null;
  pallet_count: number | null;
  destination_address: string | null;
  postcode: string | null;
  haulier_name: string | null;
  haulier_phone: string | null;
}

export interface PlannerDay {
  date: string;
  bays: PlannerBay[];
  loads: PlannerLoad[];
  unassigned: PlannerUnassignedLoad[];
}

export interface DayNote {
  day: string;
  note: string | null;
  updated_at: string | null;
}

export interface LoadSnapshot {
  id: number;
  day: string;
  load_id: number;
  load_ref: string;
  customer_id: number | null;
  customer_name: string | null;
  customer_address: string | null;
  customer_postcode: string | null;
  haulier_id: number | null;
  haulier_name: string | null;
  site_id: number | null;
  site_name: string | null;
  rate_card_no: number | null;
  status: string;
  destination_address: string | null;
  postcode: string | null;
  delivery_date: string | null;
  arrival_time: string | null;
  departure_time: string | null;
  total_pallet_count: number | null;
  total_cartons: number | null;
  depot_reference_number: string | null;
  invoice_reference: string | null;
  collection_date: string | null;
  collection_time: string | null;
  shipping_request_numbers: string | null;
  published_at: string | null;
  row_version: number;
}

export interface ChangeRequest {
  id: number;
  load_id: number;
  bay_id: number;
  start_utc: string;
  end_utc: string;
  requested_by: number | null;
  status: string;
  expires_at: string;
  decided_by: number | null;
  decided_at: string | null;
}

export interface AuditFieldChange {
  field_name: string;
  old_value: string | null;
  new_value: string | null;
}

export interface AuditLog {
  id: number;
  actor_user_id: number | null;
  occurred_at: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  correlation_id: string | null;
  ip_address: string | null;
  actor_email?: string | null;
  changes: AuditFieldChange[];
}

export interface Notification {
  id: number;
  event_type: string;
  severity: "info" | "warning" | "error" | "critical";
  title_key: string;
  body_key: string;
  params: Record<string, unknown>;
  entity_type: string | null;
  entity_id: string | null;
  aggregate_key: string | null;
  occurrence_count: number;
  created_at: string;
  read_at: string | null;
}

export interface NotificationTemplate {
  id: number;
  event_type: string;
  locale: string;
  channel: "in_app" | "email";
  subject: string | null;
  body: string;
}

export interface NotificationRule {
  id: number;
  event_type: string;
  required_permission: string | null;
  channels: string[];
  is_enabled: boolean;
}

export interface Invoice {
  id: number;
  load_id: number;
  invoice_number: string;
  amount: string | null;
  added_by: number | null;
  added_at: string;
}

export interface OutstandingInvoice {
  load_id: number;
  load_ref: string;
  customer_id: number | null;
  haulier_id: number | null;
  delivery_date: string | null;
  status: string;
}

export type InvoiceDocumentStatus = "pending" | "accepted" | "queried" | "rejected";

export type InvoiceMatchStatus =
  | "no_load"
  | "no_rate_card"
  | "no_amount"
  | "matched"
  | "mismatch";

/** An uploaded invoice PDF with extraction, match and decision state. */
export interface InvoiceDocument {
  id: number;
  import_run_id: number;
  import_source_id: number | null;
  filename: string;
  content_hash: string;
  archive_path: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  amount: string | null;
  currency: string;
  supplier_name: string | null;
  extracted_fields: Record<string, unknown>;
  haulier_id: number | null;
  load_id: number | null;
  customer_id: number | null;
  load_invoice_id: number | null;
  rate_card_id: number | null;
  expected_amount: string | null;
  match_status: InvoiceMatchStatus;
  match_detail: string | null;
  status: InvoiceDocumentStatus;
  decision_note: string | null;
  decided_by: number | null;
  decided_at: string | null;
  uploaded_at: string;
  load_ref: string | null;
  customer_name: string | null;
  haulier_name: string | null;
}

/** Manual corrections to an extracted invoice (re-matched on save). */
export interface InvoiceDocumentUpdate {
  invoice_number?: string | null;
  invoice_date?: string | null;
  amount?: string | number | null;
  currency?: string;
  supplier_name?: string | null;
}

/** A PDF field extraction rule (label and/or regex pattern). */
export interface PdfExtractor {
  id: number;
  import_type: ImportType;
  destination_field: string;
  label: string | null;
  pattern: string | null;
  is_required: boolean;
  sort_order: number;
}

export interface Language {
  id: number;
  code: string;
  display_name: string;
  native_name: string;
  direction: "ltr" | "rtl";
  is_active: boolean;
  is_primary: boolean;
  sort_order: number;
}

export interface TranslationRow {
  key_id: number;
  key: string;
  namespace: string;
  description: string | null;
  values: Record<string, string>;
  machine_translated: Record<string, boolean>;
}
